'use client'

import {
    deleteNotificationGroup,
    getNotifications,
    markAsRead,
    markGroupAsRead,
    Notification,
    NotificationGroup,
    NOTIFICATION_TYPE_COLORS,
    NOTIFICATION_TYPE_LABELS,
} from '@/lib/api/notifications'
import { format, formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useState } from 'react'
import {
    MdCampaign,
    MdCheck,
    MdCheckCircle,
    MdDeleteSweep,
    MdError,
    MdExpandLess,
    MdExpandMore,
    MdInfo,
    MdSettings,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_ICONS: Record<string, React.ReactNode> = {
    INFO: <MdInfo className="text-info" size={20} />,
    SUCCESS: <MdCheckCircle className="text-success" size={20} />,
    WARNING: <MdWarning className="text-warning" size={20} />,
    ERROR: <MdError className="text-error" size={20} />,
    SYSTEM: <MdSettings className="text-secondary" size={20} />,
    ANNOUNCEMENT: <MdCampaign className="text-secondary" size={20} />,
}

/** Quantas notificações do grupo carregar por vez ao expandir. */
const ITEMS_PER_PAGE = 10

interface NotificationGroupRowProps {
    group: NotificationGroup
    /** Rótulo legível da categoria, para a linha de contexto. */
    categoryLabel?: string
    resourceLabel?: string
    onChanged: () => void
}

function relativeDate(value: string): string {
    try {
        return formatDistanceToNow(new Date(value), {
            addSuffix: true,
            locale: ptBR,
        })
    } catch {
        return ''
    }
}

function absoluteDate(value: string): string {
    try {
        return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    } catch {
        return ''
    }
}

/**
 * Uma linha da lista = um grupo de notificações do mesmo evento.
 *
 * As notificações individuais só são buscadas quando o usuário expande. Com
 * grupos de 50+ itens, trazer tudo junto reconstruiria exatamente o problema que
 * o agrupamento existe para resolver.
 */
export default function NotificationGroupRow({
    group,
    categoryLabel,
    resourceLabel,
    onChanged,
}: NotificationGroupRowProps) {
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)
    const [items, setItems] = useState<Notification[]>([])
    const [loadingItems, setLoadingItems] = useState(false)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [acting, setActing] = useState(false)

    const groupKey = {
        module: group.module,
        resource: group.resource,
        title: group.title,
    }

    const loadItems = useCallback(
        async (targetPage: number) => {
            setLoadingItems(true)
            try {
                const response = await getNotifications({
                    ...groupKey,
                    page: targetPage,
                    limit: ITEMS_PER_PAGE,
                })
                if (response.success) {
                    setItems(prev =>
                        targetPage === 1
                            ? response.result.notifications
                            : [...prev, ...response.result.notifications]
                    )
                    setPage(targetPage)
                    setHasMore(targetPage < response.result.totalPages)
                } else {
                    toast.error(
                        response.error || 'Erro ao carregar as notificações'
                    )
                }
            } catch {
                toast.error('Erro ao carregar as notificações')
            } finally {
                setLoadingItems(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [group.module, group.resource, group.title]
    )

    const toggleExpanded = () => {
        const next = !expanded
        setExpanded(next)
        if (next && items.length === 0) {
            loadItems(1)
        }
    }

    const handleMarkGroupRead = async () => {
        setActing(true)
        try {
            const response = await markGroupAsRead(groupKey)
            if (response.success) {
                toast.success(
                    `${response.result.affected} ${
                        response.result.affected === 1
                            ? 'notificação marcada'
                            : 'notificações marcadas'
                    } como lida${response.result.affected === 1 ? '' : 's'}`
                )
                onChanged()
                if (expanded) loadItems(1)
            } else {
                toast.error(response.error || 'Erro ao marcar como lidas')
            }
        } finally {
            setActing(false)
        }
    }

    const handleDeleteGroup = async () => {
        setActing(true)
        try {
            const response = await deleteNotificationGroup(groupKey)
            if (response.success) {
                toast.success(
                    `${response.result.affected} ${
                        response.result.affected === 1
                            ? 'notificação excluída'
                            : 'notificações excluídas'
                    }`
                )
                onChanged()
            } else {
                toast.error(response.error || 'Erro ao excluir')
            }
        } finally {
            setActing(false)
        }
    }

    const openNotification = async (notification: Notification) => {
        if (!notification.isRead) {
            await markAsRead(notification.id)
            setItems(prev =>
                prev.map(n =>
                    n.id === notification.id ? { ...n, isRead: true } : n
                )
            )
            onChanged()
        }
        if (notification.link) navigate(notification.link)
    }

    const context = [categoryLabel, resourceLabel].filter(Boolean).join(' › ')
    const isSingle = group.total === 1

    return (
        <div className={group.unread > 0 ? 'bg-base-200/30' : ''}>
            <div className="flex gap-4 p-4 hover:bg-base-200/50 transition-colors">
                <div className="flex-shrink-0 mt-1">
                    {TYPE_ICONS[group.type]}
                </div>

                <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={toggleExpanded}
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3
                                    className={`font-medium ${
                                        group.unread > 0
                                            ? 'text-base-content'
                                            : 'text-base-content/70'
                                    }`}
                                >
                                    {group.title}
                                </h3>
                                {!isSingle && (
                                    <span className="badge badge-neutral badge-sm font-semibold">
                                        {group.total}
                                    </span>
                                )}
                                {group.unread > 0 && (
                                    <span className="badge badge-secondary badge-sm">
                                        {group.unread} não lida
                                        {group.unread > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            {context && (
                                <p className="text-xs text-base-content/50 mt-1">
                                    {context}
                                </p>
                            )}
                        </div>
                        <span
                            className={`badge badge-sm flex-shrink-0 ${
                                NOTIFICATION_TYPE_COLORS[group.type]
                            }`}
                        >
                            {NOTIFICATION_TYPE_LABELS[group.type]}
                        </span>
                    </div>

                    {group.lastMessage && (
                        <p className="text-sm text-base-content/60 mt-2 line-clamp-2 whitespace-pre-wrap">
                            {group.lastMessage}
                        </p>
                    )}

                    <div className="flex items-center gap-4 mt-3 text-xs text-base-content/50">
                        <span title={absoluteDate(group.lastAt)}>
                            {isSingle ? '' : 'Mais recente '}
                            {relativeDate(group.lastAt)}
                        </span>
                        <span className="flex items-center gap-1 text-secondary">
                            {expanded ? (
                                <>
                                    <MdExpandLess size={14} /> Recolher
                                </>
                            ) : (
                                <>
                                    <MdExpandMore size={14} />
                                    {isSingle
                                        ? 'Ver detalhes'
                                        : `Ver as ${group.total}`}
                                </>
                            )}
                        </span>
                    </div>
                </button>

                <div className="flex flex-col gap-2 flex-shrink-0">
                    {group.unread > 0 && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={handleMarkGroupRead}
                            disabled={acting}
                            title={
                                isSingle
                                    ? 'Marcar como lida'
                                    : `Marcar as ${group.total} como lidas`
                            }
                        >
                            <MdCheck size={16} />
                        </button>
                    )}
                    <button
                        className="btn btn-ghost btn-xs text-error"
                        onClick={handleDeleteGroup}
                        disabled={acting}
                        title={
                            isSingle
                                ? 'Excluir'
                                : `Excluir as ${group.total} do grupo`
                        }
                    >
                        <MdDeleteSweep size={16} />
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-base-300/60 bg-base-100">
                    {loadingItems && items.length === 0 ? (
                        <div className="p-4 text-center text-sm text-base-content/60">
                            <span className="loading loading-spinner loading-sm mr-2" />
                            Carregando...
                        </div>
                    ) : (
                        <ul className="divide-y divide-base-300/60">
                            {items.map(item => (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-4 py-3 pl-14 hover:bg-base-200/50 transition-colors ${
                                            !item.isRead ? 'bg-base-200/20' : ''
                                        }`}
                                        onClick={() => openNotification(item)}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <p className="text-sm text-base-content/80 whitespace-pre-wrap flex-1">
                                                {item.message}
                                            </p>
                                            {!item.isRead && (
                                                <span className="badge badge-secondary badge-xs mt-1 flex-shrink-0" />
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-base-content/50">
                                            <span
                                                title={absoluteDate(
                                                    item.createdAt
                                                )}
                                            >
                                                {relativeDate(item.createdAt)}
                                            </span>
                                            {item.sender && (
                                                <span>
                                                    por {item.sender.name}
                                                </span>
                                            )}
                                            {item.link && (
                                                <span className="text-secondary">
                                                    Clique para abrir
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {hasMore && (
                        <div className="p-3 text-center border-t border-base-300/60">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => loadItems(page + 1)}
                                disabled={loadingItems}
                            >
                                {loadingItems
                                    ? 'Carregando...'
                                    : 'Carregar mais'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
